<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        formName?: string | null
        formExtrapolation?: boolean
        idExtrapolation?: string | null
        /** true si l’étude PV existe déjà (modification), pas le simple assistant de création */
        isModification?: boolean
    }>(),
    {
        isModification: false,
    }
)

const emit = defineEmits<{
    (
        event: "click:submit",
        payload: { name: string; extrapolation: boolean }
    ): void
}>()

// ================================
// I18N
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            pvDim: {
                general: {
                    title: "Informations générales",
                    scenarioName: "Nom du scénario",
                    scenarioNamePlaceholder: "Nom",
                    nameError:
                        "Seuls les caractères alphanumériques, le point, le trait d'union et le trait de soulignement sont autorisés",
                    extrapolationTitle: "Étude avec ou sans extrapolation ?",
                    withExtrapolation: "Avec extrapolation",
                    withoutExtrapolation: "Sans extrapolation",
                    extrapolationLocked:
                        "Il n'est pas possible de changer ce champ lors d'une modification. Vous pouvez créer une nouvelle étude.",
                    next: "Suivant",
                },
            },
        },
    },
})

// ================================
// ETAT LOCAL
// ================================
const name = ref(props.formName ?? "")
const extrapolationToggle = ref(props.formExtrapolation ?? false)

watch(
    () => [props.formName, props.formExtrapolation] as const,
    ([n, ex]) => {
        if (n != null) name.value = n
        if (ex != null) extrapolationToggle.value = ex
    }
)

// ================================
// VALIDATION
// ================================
/**
 * Vérifie que le nom ne contient que des caractères autorisés (aligné legacy / contraintes AWS).
 * @param value - texte saisi
 * @returns true si valide
 */
function validateValidAwsString(value: string): boolean {
    if (!value) return false
    return /^[a-zA-Z0-9\-_. ]*$/.test(value)
}

const getNameError = computed((): string | null => {
    const v = name.value.trim()
    if (v.length === 0) return null
    if (!validateValidAwsString(v)) {
        return t("pvDim.general.nameError")
    }
    return null
})

const getCanSubmit = computed(
    () => name.value.trim().length > 0 && getNameError.value == null
)

// ================================
// INTERACTIONS
// ================================
function onClickSubmit(): void {
    emit("click:submit", {
        name: name.value.trim(),
        extrapolation: extrapolationToggle.value,
    })
}
</script>

<template>
    <section
        class="pv-form-general-info flex w-full max-w-3xl flex-col items-stretch gap-8"
    >
        <div class="flex w-full flex-col gap-4">
            <h1 class="text-lg font-medium text-primary">
                {{ t("pvDim.general.title") }}
            </h1>

            <div class="flex flex-col gap-2">
                <label
                    class="text-sm font-medium text-surface-700"
                    for="pv-scenario-name"
                >
                    {{ t("pvDim.general.scenarioName") }}
                </label>
                <BaseInputText
                    id="pv-scenario-name"
                    v-model="name"
                    class="w-full"
                    type="text"
                    :placeholder="t('pvDim.general.scenarioNamePlaceholder')"
                    :invalid="getNameError != null"
                />
                <p v-if="getNameError" class="text-sm text-red-600">
                    {{ getNameError }}
                </p>
            </div>
        </div>

        <div
            v-if="idExtrapolation"
            class="flex w-full flex-col gap-3 rounded-lg border border-surface-200 p-4"
        >
            <p class="text-sm font-medium text-surface-800">
                {{ t("pvDim.general.extrapolationTitle") }}
            </p>
            <div
                class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            >
                <div class="flex items-center gap-3">
                    <BaseInputSwitch
                        v-model="extrapolationToggle"
                        :disabled="props.isModification"
                    />
                    <span class="text-sm text-surface-700">
                        {{
                            extrapolationToggle
                                ? t("pvDim.general.withExtrapolation")
                                : t("pvDim.general.withoutExtrapolation")
                        }}
                    </span>
                </div>
            </div>
            <BaseMessage
                v-if="props.isModification"
                severity="info"
                :closable="false"
                class="w-full"
            >
                {{ t("pvDim.general.extrapolationLocked") }}
            </BaseMessage>
        </div>

        <div class="flex w-full justify-end">
            <BaseButton
                type="button"
                :label="t('pvDim.general.next')"
                :disabled="!getCanSubmit"
                @click="onClickSubmit"
            />
        </div>
    </section>
</template>
